import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Zap } from "lucide-react";
import { api, TOKEN_KEY, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { consumePendingInvite } from "@/lib/invite";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function GoogleButton() {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" className="w-full" onClick={handleGoogle} data-testid="google-login-button">
      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      Continue with Google
    </Button>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src="https://images.unsplash.com/photo-1587614382231-d1590f0039e7?auto=format&fit=crop&w=1000&h=1400&q=80" alt="Freelancer running her business with BizForge AI" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/75" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15"><Zap className="h-5 w-5" strokeWidth={2.5} /></div>
            <span className="font-display text-lg font-bold">BizForgeAI</span>
          </Link>
          <div>
            <p className="font-display text-3xl font-bold leading-tight">Your business, forged by AI.</p>
            <p className="mt-3 max-w-md text-white/80">One workspace for clients, finance, operations, and growth — with a co-pilot that understands it all.</p>
          </div>
        </div>
      </div>
      <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:min-h-0">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 lg:hidden">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary"><Zap className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} /></div>
              <span className="font-display text-lg font-bold">BizForgeAI</span>
            </Link>
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setSession(data.token, data.user);
      const joined = await consumePendingInvite();
      navigate(joined || data.user.onboarded ? "/dashboard" : "/onboarding", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your BizForge AI workspace.">
      <form onSubmit={submit} className="space-y-4">
        <GoogleButton />
        <div className="relative py-1 text-center text-xs text-muted-foreground">
          <span className="bg-background px-2 relative z-10">or continue with email</span>
          <div className="absolute left-0 top-1/2 h-px w-full bg-border" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" placeholder="you@company.com" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs font-medium text-accent hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" placeholder="••••••••" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Log in
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          No account? <Link to="/register" className="font-semibold text-accent hover:underline" data-testid="go-register-link">Sign up free</Link>
        </p>
      </form>
    </AuthShell>
  );
}
